import { useContext, useMemo } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import {
  hasRole,
  hasAnyRole,
  hasAllRoles,
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  isPlatformAdmin,
  isPlatformUser,
  isAccountManager,
  isAgent,
  canAccessAgentFeatures,
  canAccessAdminFeatures,
  getAccessibleNavigation,
  ROLES,
  PLATFORM_ROLES,
  PERMISSIONS,
} from '../utils/permissions';

/**
 * Custom hook for accessing user permissions (Phase 0 persona-aware)
 * @returns {Object} Permission checking functions and user roles/permissions
 */
export const usePermissions = () => {
  const { user, activePersona } = useContext(AuthContext);

  const permissions = useMemo(() => {
    // Platform users bypass persona checks entirely — no agency context needed
    if (isPlatformUser(user)) {
      const admin = isPlatformAdmin(user);
      return {
        roles: user?.roles || [],
        permissionsList: user?.permissions || [],
        userType: user?.user_type || null,
        platformRole: user?.platform_role || null,

        hasRole: (role) => hasRole(user, role),
        hasAnyRole: (roles) => hasAnyRole(user, roles),
        hasAllRoles: (roles) => hasAllRoles(user, roles),

        hasPermission: (permission) => hasPermission(user, permission),
        hasAnyPermission: (perms) => hasAnyPermission(user, perms),
        hasAllPermissions: (perms) => hasAllPermissions(user, perms),

        isPlatformAdmin: admin,
        isPlatformUser: true,
        isAccountManager: isAccountManager(user),
        isAgent: true, // platform users can access all agency features
        canAccessAgentFeatures: true,
        canAccessAdminFeatures: admin,

        navigation: getAccessibleNavigation(user),

        ROLES,
        PLATFORM_ROLES,
        PERMISSIONS,
      };
    }

    // Persona-based permissions (Phase 0)
    if (activePersona) {
      // Create a mock user object with persona permissions for backward compatibility
      const personaUser = {
        ...user,
        permissions: activePersona.permissions || [],
        roles: [{ name: activePersona.role_name }],
      };

      return {
        roles: [{ name: activePersona.role_name }],
        permissionsList: activePersona.permissions || [],
        userType: user?.user_type || null,

        // Permission checks use persona permissions
        hasRole: (role) => activePersona.role_name === role,
        hasAnyRole: (roles) => roles.includes(activePersona.role_name),
        hasAllRoles: (roles) => roles.length === 1 && roles[0] === activePersona.role_name,

        hasPermission: (permission) => activePersona.permissions?.includes(permission) || false,
        hasAnyPermission: (perms) => perms.some(p => activePersona.permissions?.includes(p)),
        hasAllPermissions: (perms) => perms.every(p => activePersona.permissions?.includes(p)),

        isPlatformAdmin: false,
        isPlatformUser: false,
        isAccountManager: false,
        isAgent: activePersona.role_name === 'agent',
        canAccessAgentFeatures: activePersona.role_name === 'agent' || activePersona.permissions?.some(p => p.startsWith('properties.')),
        canAccessAdminFeatures: activePersona.permissions?.some(p => p.includes('admin') || p.includes('manage')),

        navigation: getAccessibleNavigation(personaUser),

        ROLES,
        PLATFORM_ROLES,
        PERMISSIONS,
      };
    }

    // Agency user permissions (agents, owners, renters)
    return {
      roles: user?.roles || [],
      permissionsList: user?.permissions || [],
      userType: user?.user_type || null,
      platformRole: user?.platform_role || null,

      hasRole: (role) => hasRole(user, role),
      hasAnyRole: (roles) => hasAnyRole(user, roles),
      hasAllRoles: (roles) => hasAllRoles(user, roles),

      hasPermission: (permission) => hasPermission(user, permission),
      hasAnyPermission: (perms) => hasAnyPermission(user, perms),
      hasAllPermissions: (perms) => hasAllPermissions(user, perms),

      isPlatformAdmin: isPlatformAdmin(user),
      isPlatformUser: isPlatformUser(user),
      isAccountManager: isAccountManager(user),
      isAgent: isAgent(user),
      canAccessAgentFeatures: canAccessAgentFeatures(user),
      canAccessAdminFeatures: canAccessAdminFeatures(user),

      navigation: getAccessibleNavigation(user),

      ROLES,
      PLATFORM_ROLES,
      PERMISSIONS,
    };
  }, [user, activePersona]);

  return permissions;
};

export default usePermissions;
